import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useOrgBasePath } from "@/hooks/useOrgNavigate";
import { authedStream } from "@/lib/api";
import { toast } from "@/hooks/useToast";
import { trackEvent } from "@/lib/posthog";

/** A server-reported stream failure. `code` is the machine-readable case the
 * backend attaches to known errors (e.g. "pii_unavailable") so the toast can
 * be translated; `message` is the server's English fallback. */
class ChatStreamError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ChatStreamError";
  }
}

/** Panel width bounds (px) for the drag-to-resize handle. */
export const BK_MIN_WIDTH = 320;
export const BK_MAX_WIDTH = 640;
const BK_DEFAULT_WIDTH = 400;

const LS_OPEN = "bk-chat-open";
const LS_WIDTH = "bk-chat-width";
const lsThreadKey = (orgId: string) => `bk-active-thread-${orgId}`;

/** A locally-held user turn while the request is in flight (before it persists). */
export interface PendingTurn {
  userText: string;
}

interface BookkeeperChatContextValue {
  /** Whether the provider is actually mounted (false = safe no-op default). */
  isAvailable: boolean;
  isOpen: boolean;
  widthPx: number;
  activeThreadId: string | null;
  isThinking: boolean;
  pending: PendingTurn | null;
  /** Live assistant text being streamed for the in-flight turn ("" when idle). */
  streamingText: string;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setWidth: (px: number) => void;
  selectThread: (threadId: string) => void;
  newThread: () => void;
  sendMessage: (text: string) => Promise<void>;
}

const noop = () => undefined;

const defaultValue: BookkeeperChatContextValue = {
  isAvailable: false,
  isOpen: false,
  widthPx: BK_DEFAULT_WIDTH,
  activeThreadId: null,
  isThinking: false,
  pending: null,
  streamingText: "",
  open: noop,
  close: noop,
  toggle: noop,
  setWidth: noop,
  selectThread: noop,
  newThread: noop,
  sendMessage: async () => undefined,
};

const BookkeeperChatContext = createContext<BookkeeperChatContextValue>(defaultValue);

export function useBookkeeperChat(): BookkeeperChatContextValue {
  return useContext(BookkeeperChatContext);
}

function clampWidth(px: number): number {
  return Math.min(Math.max(px, BK_MIN_WIDTH), BK_MAX_WIDTH);
}

export function BookkeeperChatProvider({ children }: { children: React.ReactNode }) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id ?? null;
  const orgBasePath = useOrgBasePath();
  const queryClient = useQueryClient();
  const { t } = useTranslation("errors");

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_OPEN) === "1";
    } catch {
      return false;
    }
  });
  const [widthPx, setWidthState] = useState<number>(() => {
    try {
      const raw = Number(localStorage.getItem(LS_WIDTH));
      return Number.isFinite(raw) && raw > 0 ? clampWidth(raw) : BK_DEFAULT_WIDTH;
    } catch {
      return BK_DEFAULT_WIDTH;
    }
  });
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [pending, setPending] = useState<PendingTurn | null>(null);
  const [streamingText, setStreamingText] = useState("");

  // Restore the last active thread when the org becomes known / changes.
  useEffect(() => {
    if (!orgId) return;
    try {
      setActiveThreadId(localStorage.getItem(lsThreadKey(orgId)));
    } catch {
      setActiveThreadId(null);
    }
  }, [orgId]);

  const persistOpen = useCallback((v: boolean) => {
    setIsOpen(v);
    try {
      localStorage.setItem(LS_OPEN, v ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const open = useCallback(() => persistOpen(true), [persistOpen]);
  const close = useCallback(() => persistOpen(false), [persistOpen]);
  const toggle = useCallback(() => persistOpen(!isOpen), [persistOpen, isOpen]);

  const setWidth = useCallback((px: number) => {
    const clamped = clampWidth(px);
    setWidthState(clamped);
    try {
      localStorage.setItem(LS_WIDTH, String(clamped));
    } catch {
      /* ignore */
    }
  }, []);

  const selectThread = useCallback(
    (threadId: string) => {
      setActiveThreadId(threadId);
      if (orgId) {
        try {
          localStorage.setItem(lsThreadKey(orgId), threadId);
        } catch {
          /* ignore */
        }
      }
    },
    [orgId],
  );

  const newThread = useCallback(() => {
    setActiveThreadId(null);
    if (orgId) {
      try {
        localStorage.removeItem(lsThreadKey(orgId));
      } catch {
        /* ignore */
      }
    }
  }, [orgId]);

  // Guard against overlapping sends.
  const inFlight = useRef(false);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !orgId || inFlight.current) return;
      inFlight.current = true;
      setPending({ userText: trimmed });
      setIsThinking(true);
      setStreamingText("");
      let acc = "";
      let threadIdToUse = activeThreadId;
      let finalThreadId = threadIdToUse;
      // Set when the server ran the create_budget write tool this turn. Read
      // in `finally`, not on the success path: the write commits before the
      // reply is generated (`save_completed_reply_failed`), so the budget list
      // must refresh even when the stream ends in an error.
      let sawBudgetWrite = false;
      // Same for Ré's create_accounts (the CoA proposal card it commits): the
      // chart refreshes in `finally`, and the open thread's messages refetch
      // there anyway, so the card picks up its `committed` stamp.
      let sawAccountsWrite = false;
      // Truncation detection. Both flags live outside the `try` because the
      // `catch` reads them: a stream that dies mid-flight (function killed,
      // proxy timeout, connection dropped) surfaces as a raw `TypeError` from
      // `reader.read()`, or as a clean `done: true` with no `{type:"done"}`
      // frame — neither of which is an error today.
      let sawDone = false;
      // Load-bearing, not defensive: the `!res.ok` envelope throw below happens
      // before any stream exists and `sawDone` is false there too. Without this
      // flag, gating errors (401/403/503) and the stale-thread 404 would all be
      // reported as truncations.
      let streamOpened = false;
      // Set once the success path has already refetched, so `finally` doesn't
      // fire a second, redundant refetch of the same two keys.
      let refreshed = false;
      const postChat = () =>
        authedStream(`/api/v1/bookkeeper/orgs/${orgId}/chat`, {
          threadId: threadIdToUse ?? undefined,
          message: trimmed,
          orgBasePath,
          currentPath: typeof window !== "undefined" ? window.location.pathname : undefined,
        });
      try {
        let res = await postChat();
        // Self-heal a stale persisted thread: the stored id can outlive the row
        // (deleted in another tab, retention purge, database reset). Drop it and
        // retry once on a fresh thread instead of failing the send.
        if (res.status === 404 && threadIdToUse) {
          newThread();
          threadIdToUse = null;
          finalThreadId = null;
          res = await postChat();
        }
        if (!res.ok || !res.body) {
          // Gating errors (401/403/503/…) come back as a normal JSON envelope.
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Request failed (${res.status})`);
        }

        // Parse the SSE stream: `data: {json}\n\n` frames.
        const reader = res.body.getReader();
        streamOpened = true;
        const decoder = new TextDecoder();
        let buffer = "";
        let streamError: ChatStreamError | null = null;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const frame of frames) {
            const line = frame.split("\n").find((l) => l.startsWith("data:"));
            if (!line) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            let evt: {
              type: string;
              text?: string;
              threadId?: string;
              name?: string;
              message?: string;
              code?: string;
              chars?: number;
              write?: boolean;
            };
            try {
              evt = JSON.parse(json);
            } catch {
              continue;
            }
            if (evt.type === "delta" && evt.text) {
              acc += evt.text;
              setStreamingText(acc);
              setIsThinking(false);
            } else if (evt.type === "reset") {
              // Legacy frame — the API no longer sends it (pre-tool narration
              // is kept as part of the reply now; wiping it read as a broken
              // chat). Still honoured so a stale server can't desync the
              // bubble: drop only the flagged chars, or everything when unset.
              acc =
                typeof evt.chars === "number" ? acc.slice(0, Math.max(0, acc.length - evt.chars)) : "";
              setStreamingText(acc);
              setIsThinking(true);
            } else if (evt.type === "tool") {
              // The narration already streamed stays in the bubble; the
              // spinner shows beneath it while the tool runs.
              setIsThinking(true);
              if (evt.write === true && evt.name) trackEvent("re_write_tool_invoked", { tool: evt.name });
              if (evt.write === true && evt.name === "create_budget") sawBudgetWrite = true;
              if (evt.write === true && evt.name === "create_accounts") sawAccountsWrite = true;
            } else if (evt.type === "done") {
              // Before the threadId check on purpose: a `done` frame without a
              // threadId still means the server finished the turn.
              if (!sawDone) trackEvent("re_chat_message_sent", { new_thread: !threadIdToUse });
              sawDone = true;
              if (evt.threadId) finalThreadId = evt.threadId;
            } else if (evt.type === "error") {
              streamError = new ChatStreamError(evt.message ?? "Assistant error", evt.code);
            }
          }
        }
        // Order matters: an explicit server `error` frame is the last frame of
        // the stream and is never followed by `done`, so checking `!sawDone`
        // first would turn every server-reported failure into a truncation.
        if (streamError) throw streamError;
        if (!sawDone) throw new ChatStreamError("The answer was cut short.", "stream_truncated");

        if (finalThreadId) selectThread(finalThreadId);
        // Refetch the persisted turn, THEN drop the optimistic/streamed bubbles,
        // so the message doesn't flicker out and back in.
        await queryClient.invalidateQueries({ queryKey: ["bookkeeper-messages", finalThreadId] });
        await queryClient.invalidateQueries({ queryKey: ["bookkeeper-threads", orgId] });
        refreshed = true;
      } catch (e) {
        // Known error codes get a translated description; anything else falls
        // back to the server's message (already user-safe English).
        // A stream that opened and then died without a `done` frame was cut
        // short — even when it surfaces as a raw `TypeError` from `read()`.
        // An explicit ChatStreamError already carries its own code.
        const truncated = streamOpened && !sawDone && !(e instanceof ChatStreamError);
        const code = truncated
          ? "stream_truncated"
          : e instanceof ChatStreamError
            ? e.code
            : undefined;
        const description =
          code === "stream_truncated"
            ? t("bookkeeperStreamTruncated")
            : code === "pii_unavailable"
              ? t("bookkeeperPiiUnavailable")
              : code === "save_completed_reply_failed"
                ? // The write landed before the reply failed — point at where it
                  // landed, not at the emission-factor tables by default.
                  t(
                    sawBudgetWrite
                      ? "bookkeeperBudgetCreatedReplyFailed"
                      : sawAccountsWrite
                        ? "bookkeeperAccountsCreatedReplyFailed"
                        : "bookkeeperSaveCompletedReplyFailed",
                  )
                : e instanceof Error
                  ? e.message
                  : t("genericError");
        toast.error(t("bookkeeperAssistantError"), { description });
      } finally {
        if (sawBudgetWrite) {
          void queryClient.invalidateQueries({ queryKey: ["envelopeBudgets", orgId] });
        }
        if (sawAccountsWrite) {
          // The grid + pickers (['chartOfAccounts', orgId] prefix) and a
          // parent's children list — what the Create button's hook refreshes.
          void queryClient.invalidateQueries({ queryKey: ["chartOfAccounts", orgId] });
          void queryClient.invalidateQueries({ queryKey: ["accountChildren", orgId] });
        }
        // Refetch the open thread's messages on EVERY exit, not just the
        // success path: a truncated stream still persisted the turn server-side,
        // and `pending`/`streamingText` are wiped just below — without this the
        // user's own question disappears off screen. Known limitation: on a
        // brand-new thread there is no `done` frame, so there is no id to
        // refetch and the panel stays on the empty state until History is used.
        if (!refreshed) {
          const openThreadId = finalThreadId ?? threadIdToUse;
          if (openThreadId) {
            void queryClient.invalidateQueries({
              queryKey: ["bookkeeper-messages", openThreadId],
            });
          }
          void queryClient.invalidateQueries({ queryKey: ["bookkeeper-threads", orgId] });
        }
        setPending(null);
        setStreamingText("");
        setIsThinking(false);
        inFlight.current = false;
      }
    },
    [orgId, activeThreadId, orgBasePath, queryClient, selectThread, newThread, t],
  );

  const value = useMemo<BookkeeperChatContextValue>(
    () => ({
      isAvailable: true,
      isOpen,
      widthPx,
      activeThreadId,
      isThinking,
      pending,
      streamingText,
      open,
      close,
      toggle,
      setWidth,
      selectThread,
      newThread,
      sendMessage,
    }),
    [isOpen, widthPx, activeThreadId, isThinking, pending, streamingText, open, close, toggle, setWidth, selectThread, newThread, sendMessage],
  );

  return <BookkeeperChatContext.Provider value={value}>{children}</BookkeeperChatContext.Provider>;
}
